class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      
      // Get the line index to remove
      const lineIndex = this.dataset.index;
      
      // Check if this item has decorations that need to be removed
      cartItems.removeItemWithDecorations(lineIndex);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    // Determine if we're in the main cart or drawer
    this.isMainCart = this.classList.contains('page-widthx') || document.getElementById('main-cart-items') !== null;
    
    // For DRAWER: Use debounced onChange for instant updates
    if (!this.isMainCart) {
      this.debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, 300);
      this.addEventListener('change', this.debouncedOnChange.bind(this));
    } else {
      // For MAIN CART: Listen for Update Cart button
      setTimeout(() => {
        const updateButton = document.getElementById('update-cart');
        
        if (updateButton) {
          updateButton.addEventListener('click', (event) => {
            event.preventDefault();
            this.updateAllQuantities();
          });
        }
      }, 100);
    }
  }

  onChange(event) {
    // Only used in cart drawer
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target);
  }

  /**
   * Remove an item and all its associated decorations
   */
  async removeItemWithDecorations(lineIndex) {
    // console.log(`🗑️ Removing item at line ${lineIndex} with decorations...`);
    
    try {
      // Get current cart state
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Find the item being removed
      const itemToRemove = cart.items.find((item, index) => (index + 1).toString() === lineIndex);
      
      if (!itemToRemove) {
        console.error('Item not found at index', lineIndex);
        return;
      }
      
      // console.log('Item to remove:', itemToRemove);
      
      // Collect all line indices to remove (parent + children)
      const linesToRemove = [parseInt(lineIndex)];
      
      // Check if this is a parent item (has decoration children)
      // Note: property uses dash, not underscore
      const parentKey = itemToRemove.properties?.['_line-item-key'] || itemToRemove.key;
      const isParent = itemToRemove.properties?._role === 'parent';
      const isChild = itemToRemove.properties?._role === 'child';
      const parentLineKey = itemToRemove.properties?._parent_line_key;
      
      // If this is a child, find and remove the parent too
      if (isChild && parentLineKey) {
        // console.log('  This is a child item. Finding parent...');
        cart.items.forEach((item, index) => {
          const itemKey = item.properties?.['_line-item-key'] || item.key;
          if (item.properties?._role === 'parent' && itemKey === parentLineKey) {
            const parentLine = index + 1;
            if (!linesToRemove.includes(parentLine)) {
              linesToRemove.push(parentLine);
              // console.log(`  Found parent at line ${parentLine}`);
            }
          }
        });
        
        // Also find sibling children of the same parent
        cart.items.forEach((item, index) => {
          if (item.properties?._role === 'child' && 
              item.properties?._parent_line_key === parentLineKey) {
            const siblingLine = index + 1;
            if (!linesToRemove.includes(siblingLine)) {
              linesToRemove.push(siblingLine);
              // console.log(`  Found sibling child at line ${siblingLine}`);
            }
          }
        });
      }
      
      // If this is a parent, find all child decoration items
      if (isParent && parentKey) {
        cart.items.forEach((item, index) => {
          if (item.properties?._role === 'child' && 
              item.properties?._parent_line_key === parentKey) {
            linesToRemove.push(index + 1);
            // console.log(`  Found child decoration at line ${index + 1}`);
          }
        });
      }
      
      // console.log('Lines to remove:', linesToRemove);
      
      // Remove all items (in reverse order to maintain indices)
      linesToRemove.sort((a, b) => b - a);
      
      for (const line of linesToRemove) {
        await this.removeItemByLine(line);
      }
      
      // After removing items, check if we need to remove the second logo fee
      await this.checkAndRemoveSecondLogoFee();
      
      // Reload the appropriate section
      if (this.isMainCart) {
        // For main cart, reload the page
        window.location.reload();
      } else {
        // For drawer, reload the section
        await this.reloadCartDrawer();
      }
      
    } catch (error) {
      console.error('Error removing item with decorations:', error);
      this.showError();
    }
  }

  /**
   * Remove a single item by line number
   */
  async removeItemByLine(line) {
    const body = JSON.stringify({
      line: line,
      quantity: 0
    });

    const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body
    });

    return response.json();
  }

  /**
   * Check if second logo fee should be removed
   */
  async checkAndRemoveSecondLogoFee() {
    // console.log('🔍 Checking if second logo fee should be removed...');
    
    try {
      // Get current cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Check if any items have a second decoration
      let hasSecondDecoration = false;
      let secondLogoFeeIndex = null;
      
      cart.items.forEach((item, index) => {
        // Check if this is the second logo fee
        if (item.properties?._is_second_logo_fee === 'true') {
          secondLogoFeeIndex = index + 1;
          // console.log(`  Found second logo fee at line ${secondLogoFeeIndex}`);
        }
        
        // Check if this is a parent item with second decoration
        if (item.properties?._role === 'parent' && 
            item.properties?._decoration2_variant_id) {
          hasSecondDecoration = true;
          // console.log(`  Found item with second decoration: ${item.title}`);
        }
      });
      
      // If no items have second decoration but fee exists, remove it
      if (!hasSecondDecoration && secondLogoFeeIndex) {
        // console.log('✅ No items with second decoration found. Removing second logo fee...');
        await this.removeItemByLine(secondLogoFeeIndex);
      } else if (hasSecondDecoration) {
        // console.log('✅ Items with second decoration still exist. Keeping fee.');
      } else {
        // console.log('ℹ️ No second logo fee found in cart.');
      }
      
    } catch (error) {
      console.error('Error checking second logo fee:', error);
    }
  }

  /**
   * Reload cart drawer section
   */
  async reloadCartDrawer() {
    try {
      const sections = this.getSectionsToRender().map(section => section.section);
      
      const response = await fetch(`${window.Shopify.routes.root}?sections=${sections.join(',')}`);
      const data = await response.json();
      
      this.getSectionsToRender().forEach((section) => {
        const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || 
                                 document.getElementById(section.id);
        if (elementToReplace && data[section.section]) {
          elementToReplace.innerHTML = this.getSectionInnerHTML(data[section.section], section.selector);
        }
      });
      
    } catch (error) {
      console.error('Error reloading cart drawer:', error);
    }
  }

  /**
   * Check if any items in cart have a second decoration
   */
  checkSecondDecorationStatus() {
    const allInputs = Array.from(this.querySelectorAll('[name="updates[]"]'));
    
    let hasSecondDecoration = false;
    let secondLogoFeeIndex = null;
    
    allInputs.forEach((input) => {
      const cartItemRow = input.closest('tr.cart-item');
      if (!cartItemRow) return;
      
      const isSecondLogoFee = input.hasAttribute('data-is-second-logo-fee');
      if (isSecondLogoFee) {
        secondLogoFeeIndex = input.dataset.index;
      }
    });
    
    return { hasSecondDecoration, secondLogoFeeIndex };
  }

  /**
   * Main cart: Update all quantities when button is clicked
   */
  updateAllQuantities() {
    // console.log('🔄 UPDATE ALL QUANTITIES STARTED');
    
    const allInputs = Array.from(this.querySelectorAll('[name="updates[]"]'));
    
    const updates = [];
    const processedParents = new Set();
    let hasSecondDecoration = false;
    let secondLogoFeeIndex = null;
    
    // First pass: Find all parent items and check for second decorations
    allInputs.forEach(input => {
      const index = input.dataset.key;
      const newQuantity = parseInt(input.value);
      const parentKey = input.getAttribute('data-parent');
      const childKey = input.getAttribute('data-child');
      const isSecondLogoFee = input.hasAttribute('data-is-second-logo-fee');
      const hasDecoration2 = input.hasAttribute('data-has-decoration2');
      
      if (parentKey && hasDecoration2) {
        hasSecondDecoration = true;
      }
      
      if (isSecondLogoFee) {
        secondLogoFeeIndex = index;
      }
      
      if (parentKey && !childKey && !isSecondLogoFee) {
        processedParents.add(parentKey);
        
        const children = allInputs.filter(inp => 
          inp.getAttribute('data-child') === parentKey
        );
        
        updates.push({
          type: 'parent',
          index: index,
          quantity: newQuantity,
          parentKey: parentKey,
          children: children.map(child => ({
            index: child.dataset.key,
            input: child
          }))
        });
      }
      else if (!parentKey && !childKey && !isSecondLogoFee) {
        updates.push({
          type: 'standalone',
          index: index,
          quantity: newQuantity
        });
      }
      else if (childKey) {
        if (!processedParents.has(childKey)) {
          updates.push({
            type: 'orphan-child',
            index: index,
            quantity: newQuantity
          });
        }
      }
    });
    
    if (updates.length === 0) {
      // console.log('No updates needed');
      return;
    }
    
    this.enableLoadingAll();
    
    this.performBulkUpdate(updates)
      .then(() => {
        // console.log('✅ Bulk update successful, reloading page...');
        window.location.reload();
      })
      .catch(error => {
        console.error('❌ Bulk update failed:', error);
        this.disableLoadingAll();
        this.showError();
      });
  }

  performBulkUpdate(updates) {
    // console.log(`🔄 Performing bulk update for ${updates.length} items...`);
    
    const updateData = {};
    
    updates.forEach(update => {
      if (update.type === 'parent') {
        updateData[update.index] = update.quantity;
        update.children.forEach(child => {
          updateData[child.index] = update.quantity;
        });
      } else {
        updateData[update.index] = update.quantity;
      }
    });
    
    // console.log('Update data:', updateData);
    
    return new Promise((resolve, reject) => {
      fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates: updateData })
      })
        .then(response => response.json())
        .then(data => {
          // console.log('Cart update response:', data);
          resolve(data);
        })
        .catch(error => {
          console.error('Cart update error:', error);
          reject(error);
        });
    });
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      }
    ];

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems) {
      sections.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents'
      });
    }

    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '.js-contents'
      });
    }

    return sections;
  }

  /**
   * CART DRAWER ONLY: Update quantity with AJAX
   */
  updateQuantity(line, quantity, name, inputElement) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${window.Shopify.routes.root}cart/change.js`, {...fetchConfig(), ...{ body }})
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const bodyEl = document.querySelector('body');
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML =
            this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }));

        this.updateLiveRegions(line, parsedState.item_count);
        const lineItem =  document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }
        this.disableLoading();
      }).catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
        this.disableLoading();
      });
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
      const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);

      lineItemError
        .querySelector('.cart-item__error-text')
        .innerHTML = window.cartStrings.quantityError.replace(
          '[quantity]',
          quantityElement.value
        );
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  enableLoadingAll() {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');
    
    this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.remove('hidden'));
    
    const updateButton = document.getElementById('update-cart');
    if (updateButton) {
      updateButton.disabled = true;
      updateButton.textContent = 'Updating...';
    }
    
    document.activeElement.blur();
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }
  }

  disableLoading() {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');
  }

  disableLoadingAll() {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');
    
    this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
    
    const updateButton = document.getElementById('update-cart');
    if (updateButton) {
      updateButton.disabled = false;
      updateButton.textContent = 'Update Cart';
    }
  }

  showError() {
    const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errors) {
      errors.textContent = window.cartStrings.error || 'An error occurred updating your cart.';
    }
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${window.Shopify.routes.root}cart/update.js`, {...fetchConfig(), ...{ body }});
      }, 300))
    }
  });
};

// console.log('🛒 CART.JS LOADED - WITH DECORATION DELETION SUPPORT');